import { codeFont, numFont } from '../../utils/fonts';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Snackbar,
  LinearProgress,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InventoryIcon from '@mui/icons-material/Inventory';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SupplierOrderStatusBadge from './SupplierOrderStatusBadge';
import SupplierOrderReceiveDialog from './SupplierOrderReceiveDialog';
import { formatPrice } from '../../utils/vat';
import { formatQuantity, formatDate } from '../../utils';

const dialogPaperProps = {
  sx: {
    borderRadius: { xs: '12px', sm: '16px' },
    m: { xs: 2, sm: 4 },
    maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
  },
};

// Full screen on a phone: the order lines need every pixel of width.
const fullScreenPaperProps = { sx: { borderRadius: 0, m: 0, maxHeight: '100%' } };

const terminalStatuses = ['delivered', 'cancelled'];

function SupplierOrderDetailDialog({ open, onClose, orderId, onStatusUpdated, t }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Sähköpostitilaus: what was sent, and sending it again.
  const [showEmailText, setShowEmailText] = useState(false);
  const [resending, setResending] = useState(false);

  const handleResendEmail = async () => {
    setResending(true);
    try {
      const response = await axios.post(`/api/supplier-orders/${orderId}/resend-email/`);
      setOrder(response.data);
      setSnackbar({ open: true, message: t('purchaseOrders.resendSuccess'), severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('purchaseOrders.resendFailed'),
        severity: 'error',
      });
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (open && orderId) {
      fetchOrder();
    }
    if (!open) {
      setOrder(null);
      setError(null);
    }
  }, [open, orderId]);

  const fetchOrder = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/supplier-orders/${orderId}/`);
      setOrder(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('purchaseOrders.statusUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = (status) => {
    setPendingStatus(status);
    setConfirmDialogOpen(true);
    setStatusMenuAnchor(null);
  };

  const handleStatusConfirm = async () => {
    setStatusUpdateLoading(true);
    try {
      await axios.patch(`/api/supplier-orders/${orderId}/`, { status: pendingStatus });
      await fetchOrder();
      if (onStatusUpdated) onStatusUpdated();
      setSnackbar({ open: true, message: t('purchaseOrders.statusUpdated'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: t('purchaseOrders.statusUpdateFailed'), severity: 'error' });
    } finally {
      setStatusUpdateLoading(false);
      setConfirmDialogOpen(false);
      setPendingStatus(null);
    }
  };

  const handleReceiveSuccess = ({ receivedCount, inventoryCount }) => {
    let message = t('purchaseOrders.receiveSuccess', { count: receivedCount });
    if (inventoryCount > 0) {
      message += '. ' + t('purchaseOrders.inventoryUpdated', { count: inventoryCount });
    }
    setSnackbar({ open: true, message, severity: 'success' });
    fetchOrder();
    if (onStatusUpdated) onStatusUpdated();
  };

  const canReceive = order && ['pending', 'confirmed', 'partially_delivered'].includes(order.status);

  const getStatusLabel = (status) => {
    return t('purchaseOrders.status.' + status);
  };

  const hasPartialDeliveries = order?.lines?.some(
    (l) => parseFloat(l.delivered_quantity) > 0
  );

  const total = order?.lines?.reduce((sum, line) => {
    const price = parseFloat(line.purchase_price) || 0;
    const qty = parseFloat(line.quantity) || 0;
    return sum + price * qty;
  }, 0) || 0;

  const workOrders = order?.work_orders || [];
  const partsSales = order?.parts_sales || [];
  // Lines ordered to the shelf (no work order, no lähete): the order builder's stock lines.
  const stockLineCount = (order?.lines || []).filter((line) => line.destination === 'stock').length;
  // Lines are grouped by source when the order spans more than one work order / lähete.
  const multiSource = workOrders.length + partsSales.length + (stockLineCount > 0 ? 1 : 0) > 1;
  const displayRef = order?.display_reference || '';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={fullScreen ? fullScreenPaperProps : dialogPaperProps}
      >
        {/* Title */}
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {order ? t('purchaseOrders.detailTitle', { reference: displayRef }) : '...'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {loading && (
            <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {error && !loading && (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {order && !loading && (
            <Box>
              {/* Atoy banners (overdue / runkotuotteita) — only visible when raw_response carries the corresponding markers */}
              {(() => {
                const raw = order.raw_response || {};
                const confirmation = raw.confirmation || {};
                const errBlock = raw.error;
                const banners = [];

                if (errBlock?.message) {
                  banners.push(
                    <Alert key="err" severity="error" sx={{ mx: { xs: 2, sm: 3 }, mt: 2 }}>
                      {errBlock.message}
                    </Alert>
                  );
                }
                if (confirmation.overdue_at && !confirmation.received_at) {
                  banners.push(
                    <Alert key="overdue" severity="warning" sx={{ mx: { xs: 2, sm: 3 }, mt: 2 }}>
                      {t('purchaseOrders.atoyOverdueBanner')}
                    </Alert>
                  );
                }
                // Runkotuotteita: TILVAHVRIV rows whose tilauksen_rivinro
                // doesn't match any submitted line. Submitted lines have
                // rivinro 20000+ in our adapter.
                const tvParsed = confirmation.tilausvahvistus_parsed;
                if (tvParsed?.lines) {
                  const submittedRivinros = new Set(
                    (order.lines || []).map((_, i) => 20000 + i)
                  );
                  const extraCodes = tvParsed.lines
                    .filter((l) => !submittedRivinros.has(l.tilauksen_rivinro))
                    .map((l) => l.tuotekoodi)
                    .filter(Boolean);
                  if (extraCodes.length) {
                    banners.push(
                      <Alert key="runko" severity="info" sx={{ mx: { xs: 2, sm: 3 }, mt: 2 }}>
                        {t('purchaseOrders.atoyRunkotuotteita', {
                          codes: extraCodes.join(', '),
                        })}
                      </Alert>
                    );
                  }
                }
                return banners;
              })()}

              {/* Sähköpostitilaus: the order went out as an email. Show to whom and when, the
                  text on demand, and let the user send it again (vendor address may have changed). */}
              {order.email_delivery && (
                <Alert
                  severity="info"
                  icon={<EmailOutlinedIcon />}
                  sx={{ mx: { xs: 2, sm: 3 }, mt: 2, '& .MuiAlert-message': { width: '100%' } }}
                  data-testid="order-email-banner"
                  action={
                    order.status !== 'cancelled' ? (
                      <Button
                        color="inherit"
                        size="small"
                        onClick={handleResendEmail}
                        disabled={resending}
                        data-testid="order-resend-email"
                      >
                        {t('purchaseOrders.resendEmail')}
                      </Button>
                    ) : null
                  }
                >
                  {t('purchaseOrders.emailSentBanner', {
                    email: order.email_delivery.to,
                    date: order.email_delivery.sent_at ? formatDate(order.email_delivery.sent_at) : '',
                  })}
                  {order.email_delivery.confirmation_sent_to && (
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      {t('purchaseOrders.emailConfirmationSentTo', { email: order.email_delivery.confirmation_sent_to })}
                    </Typography>
                  )}
                  {order.email_delivery.resent?.length > 0 && (
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      {t('purchaseOrders.emailResentCount', { count: order.email_delivery.resent.length })}
                    </Typography>
                  )}
                  <Button
                    size="small"
                    onClick={() => setShowEmailText((v) => !v)}
                    sx={{ p: 0, mt: 0.5, minWidth: 0, textTransform: 'none' }}
                    data-testid="order-toggle-email-text"
                  >
                    {showEmailText ? t('purchaseOrders.hideMessage') : t('purchaseOrders.showMessage')}
                  </Button>
                  {showEmailText && (
                    <Box
                      component="pre"
                      data-testid="order-email-text"
                      sx={{
                        mt: 1,
                        p: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        fontSize: '12px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'inherit',
                        maxHeight: 320,
                        overflow: 'auto',
                      }}
                    >
                      {order.email_delivery.text}
                    </Box>
                  )}
                </Alert>
              )}

              {/* A lähete parked on this order: receiving it from the lähete takes
                  the quantities from the document and closes both at once. The
                  manual Vastaanota stays available (paper before email happens). */}
              {(order.pending_inbound_notes || []).length > 0 && (
                <Alert
                  severity="warning"
                  icon={<MoveToInboxIcon />}
                  sx={{ mx: { xs: 2, sm: 3 }, mt: 2 }}
                  data-testid="order-pending-inbound-banner"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      data-testid="order-open-inbound-note"
                      onClick={() => {
                        onClose();
                        navigate(`/supplier-orders?tab=inbound&note=${order.pending_inbound_notes[0].id}`);
                      }}
                    >
                      {t('purchaseOrders.openInboundNote')}
                    </Button>
                  }
                >
                  {t('purchaseOrders.pendingInboundBanner', {
                    numbers: order.pending_inbound_notes.map((n) => n.lahete_number || `#${n.id}`).join(', '),
                  })}
                </Alert>
              )}

              {/* Double-booking visibility: this order already received deliveries
                  through inbound delivery notes (sähköiset lähetteet) */}
              {(order.linked_inbound_notes || []).length > 0 && (
                <Alert severity="info" sx={{ mx: { xs: 2, sm: 3 }, mt: 2 }}>
                  {t('inboundDeliveries.orderLinkedNotesWarning', {
                    numbers: order.linked_inbound_notes
                      .map((n) => n.lahete_number || `#${n.id}`)
                      .join(', '),
                  })}
                </Alert>
              )}

              {/* Status bar */}
              <Paper
                elevation={0}
                sx={{
                  mx: { xs: 2, sm: 3 },
                  mt: 2,
                  mb: 2,
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <SupplierOrderStatusBadge status={order.status} />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {canReceive && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setReceiveDialogOpen(true)}
                      startIcon={<InventoryIcon />}
                      sx={{ fontWeight: 600 }}
                    >
                      {t('purchaseOrders.receive')}
                    </Button>
                  )}
                  {!terminalStatuses.includes(order.status) && (
                    <IconButton
                      size="small"
                      onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Paper>

              {/* Order info */}
              <Box sx={{ px: { xs: 2, sm: 3 }, mb: 2 }}>
                <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.08em' }}>
                  {t('purchaseOrders.orderInfo')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('purchaseOrders.supplier')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{order.supplier_display_name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('purchaseOrders.orderNumber')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, ...numFont }}>{displayRef}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('purchaseOrders.orderDate')}</Typography>
                    <Typography variant="body2">{formatDate(order.ordered_at)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('purchaseOrders.orderer')}</Typography>
                    <Typography variant="body2">{order.ordered_by_name}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Work order cards */}
              {workOrders.length > 0 && (
                <Box sx={{ px: { xs: 2, sm: 3 }, mb: 2 }}>
                  <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.08em' }}>
                    {workOrders.length > 1 ? t('purchaseOrders.workOrdersSection') : t('purchaseOrders.workOrderSection')}
                  </Typography>
                  {workOrders.map((wo) => (
                    <Paper
                      key={wo.maintenance_id}
                      elevation={0}
                      onClick={() => {
                        onClose();
                        navigate(`/maintenances/${wo.maintenance_id}`);
                      }}
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: 'background.default',
                        borderRadius: 2,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <DirectionsCarIcon sx={{ color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {wo.vehicle_registration} #{wo.work_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {wo.vehicle_manufacturer} {wo.vehicle_model}
                          {wo.customer_name && ` — ${wo.customer_name}`}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Lähete cards (delivery notes whose special-order lines ride on this order) */}
              {partsSales.length > 0 && (
                <Box sx={{ px: { xs: 2, sm: 3 }, mb: 2 }} data-testid="order-detail-parts-sales">
                  <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.08em' }}>
                    {partsSales.length > 1 ? t('purchaseOrders.deliveryNotesSection') : t('purchaseOrders.deliveryNoteSection')}
                  </Typography>
                  {partsSales.map((sale) => (
                    <Paper
                      key={sale.parts_sale_id}
                      elevation={0}
                      onClick={() => {
                        onClose();
                        navigate(`/lahetteet/${sale.parts_sale_id}`);
                      }}
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: 'background.default',
                        borderRadius: 2,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <ReceiptLongIcon sx={{ color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t('purchaseOrders.pendingParts.deliveryNote', { number: sale.number })}
                        </Typography>
                        {sale.customer_name && (
                          <Typography variant="caption" color="text.secondary">
                            {sale.customer_name}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Order lines */}
              <Box sx={{ px: { xs: 2, sm: 3 }, mb: 2 }}>
                <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.08em' }}>
                  {t('purchaseOrders.orderedParts')}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {/* Group lines by source (work order / lähete) when the order spans several */}
                  {multiSource && order.lines?.length > 0 && (() => {
                    const groups = {};
                    order.lines.forEach((line) => {
                      const isStock = line.destination === 'stock';
                      const key = line.parts_sale_id
                        ? `s-${line.parts_sale_id}`
                        : isStock
                          ? 'stock'
                          : `w-${line.work_number || '_'}`;
                      if (!groups[key]) {
                        groups[key] = {
                          label: line.parts_sale_id
                            ? t('purchaseOrders.pendingParts.deliveryNote', { number: line.parts_sale_number })
                            : isStock
                              ? t('purchaseOrders.stockLinesSection')
                              : `#${line.work_number || ''}`,
                          lines: [],
                        };
                      }
                      groups[key].lines.push(line);
                    });
                    return Object.entries(groups).map(([key, group]) => (
                      <React.Fragment key={key}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mt: 1, display: 'block' }}>
                          {group.label}
                        </Typography>
                        {group.lines.map((line, index) => renderLine(line, index))}
                      </React.Fragment>
                    ));
                  })()}
                  {/* Single source: just render lines flat */}
                  {!multiSource && order.lines?.map((line, index) => renderLine(line, index))}
                  {/* Total row */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      pt: 1.5,
                      mt: 0.5,
                      borderTop: 2,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('purchaseOrders.totalExclVat')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatPrice(total)} €
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Comment */}
              {order.comment && (
                <Box sx={{ px: { xs: 2, sm: 3 }, mb: 2 }}>
                  <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.08em' }}>
                    {t('purchaseOrders.commentSection')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                    {order.comment}
                  </Typography>
                </Box>
              )}

              {/* Delivery history */}
              {order.deliveries?.length > 0 && (
                <Box sx={{ px: { xs: 2, sm: 3 }, mb: 2 }}>
                  <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.08em' }}>
                    {t('purchaseOrders.deliveryHistory')}
                  </Typography>
                  {order.deliveries.map((delivery) => (
                    <Paper
                      key={delivery.id}
                      elevation={0}
                      sx={{ mt: 1, p: 1.5, bgcolor: 'background.default', borderRadius: 2 }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {formatDate(delivery.received_at)}
                        </Typography>
                        {delivery.received_by_name && (
                          <Typography variant="caption" color="text.secondary">
                            {delivery.received_by_name}
                          </Typography>
                        )}
                      </Box>
                      {delivery.lines?.map((dl) => (
                        <Typography key={dl.id} variant="caption" color="text.secondary" display="block">
                          {dl.article_name}: {formatQuantity(dl.quantity_received)}
                          {dl.inventory_transaction && (
                            <Chip label={t('purchaseOrders.inventoryTracked')} size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Typography>
                      ))}
                      {delivery.notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }} display="block">
                          {delivery.notes}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <Button onClick={onClose}>{t('Close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Status change menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
      >
        {order?.status === 'pending' && (
          <MenuItem onClick={() => handleStatusClick('confirmed')}>
            {t('purchaseOrders.markConfirmed')}
          </MenuItem>
        )}
        {order?.status !== 'cancelled' && (
          <MenuItem onClick={() => handleStatusClick('cancelled')}>
            {t('purchaseOrders.cancelOrder')}
          </MenuItem>
        )}
      </Menu>

      {/* Confirm status change dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => { setConfirmDialogOpen(false); setPendingStatus(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('purchaseOrders.confirmStatusChange', {
          reference: displayRef,
          status: pendingStatus ? getStatusLabel(pendingStatus) : '',
        })}</DialogTitle>
        {pendingStatus === 'cancelled' && hasPartialDeliveries && (
          <Box sx={{ px: 3 }}>
            <Alert severity="warning" sx={{ mb: 1 }}>
              {t('purchaseOrders.cancelWarningPartialDelivery')}
            </Alert>
          </Box>
        )}
        <DialogActions>
          <Button
            onClick={() => { setConfirmDialogOpen(false); setPendingStatus(null); }}
            disabled={statusUpdateLoading}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusConfirm}
            disabled={statusUpdateLoading}
          >
            {statusUpdateLoading ? <CircularProgress size={24} /> : t('Yes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive dialog */}
      <SupplierOrderReceiveDialog
        open={receiveDialogOpen}
        onClose={() => setReceiveDialogOpen(false)}
        order={order}
        onSuccess={handleReceiveSuccess}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function renderLine(line, index) {
  const delivered = parseFloat(line.delivered_quantity) || 0;
  const ordered = parseFloat(line.quantity) || 0;
  const progress = ordered > 0 ? (delivered / ordered) * 100 : 0;

  return (
    <Box
      key={line.id || index}
      sx={{
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {line.article_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ...codeFont }}>
            {line.article_id}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', ml: 2, flexShrink: 0 }}>
          <Typography variant="body2">
            {formatQuantity(line.quantity)} x {formatPrice(line.purchase_price)} €
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatPrice(parseFloat(line.quantity) * parseFloat(line.purchase_price))} €
          </Typography>
        </Box>
      </Box>
      {delivered > 0 && (
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(progress, 100)}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
            color={progress >= 100 ? 'success' : 'primary'}
          />
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {formatQuantity(delivered)}/{formatQuantity(ordered)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default withTranslation()(SupplierOrderDetailDialog);
